import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, AlertTriangle, Shield, RotateCcw, Clock, Building2, Loader2, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataManagement = () => {
  const [bulkDeleteEnabled, setBulkDeleteEnabled] = useState(() => {
    return localStorage.getItem('bulk_delete_enabled') === 'true';
  });
  const [recycleBin, setRecycleBin] = useState([]);
  const [loadingBin, setLoadingBin] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // iter286 — Accounts recycle bin (mirrors the properties state above).
  const [acctRecycleBin, setAcctRecycleBin] = useState([]);
  const [acctLoadingBin, setAcctLoadingBin] = useState(false);
  const [acctActionLoading, setAcctActionLoading] = useState(null);
  const [acctSearchQuery, setAcctSearchQuery] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchRecycleBin = useCallback(async () => {
    try {
      setLoadingBin(true);
      const res = await fetch(`${API_URL}/api/recycle-bin/properties`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRecycleBin(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch recycle bin:', err);
    } finally {
      setLoadingBin(false);
    }
  }, []);

  useEffect(() => { fetchRecycleBin(); }, [fetchRecycleBin]);

  // iter286 — fetch Accounts recycle bin alongside Properties.
  const fetchAcctRecycleBin = useCallback(async () => {
    try {
      setAcctLoadingBin(true);
      const res = await fetch(`${API_URL}/api/recycle-bin/accounts`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAcctRecycleBin(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts recycle bin:', err);
    } finally {
      setAcctLoadingBin(false);
    }
  }, []);
  useEffect(() => { fetchAcctRecycleBin(); }, [fetchAcctRecycleBin]);

  // iter286 — Accounts restore / permanent-delete / empty-all handlers.
  const handleAcctRestore = async (recycleId, name) => {
    if (!window.confirm(`Restore "${name}" back to Accounts?`)) return;
    try {
      setAcctActionLoading(recycleId);
      const res = await fetch(`${API_URL}/api/recycle-bin/accounts/${recycleId}/restore`, { method: 'POST', headers });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (res.ok) {
        setAcctRecycleBin(prev => prev.filter(item => item.id !== recycleId));
      } else {
        alert(data.detail || 'Failed to restore account');
      }
    } catch { alert('Failed to restore account'); }
    finally { setAcctActionLoading(null); }
  };
  const handleAcctPermanentDelete = async (recycleId, name) => {
    if (!window.confirm(`PERMANENTLY delete "${name}"? This cannot be undone.`)) return;
    try {
      setAcctActionLoading(recycleId);
      const res = await fetch(`${API_URL}/api/recycle-bin/accounts/${recycleId}`, { method: 'DELETE', headers });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (res.ok) {
        setAcctRecycleBin(prev => prev.filter(item => item.id !== recycleId));
      } else {
        alert(data.detail || 'Failed to delete account');
      }
    } catch { alert('Failed to delete account'); }
    finally { setAcctActionLoading(null); }
  };
  const handleAcctEmptyBin = async () => {
    if (!window.confirm(`Empty Accounts recycle bin? All ${acctRecycleBin.length} accounts will be permanently deleted.`)) return;
    try {
      setAcctActionLoading('empty-all');
      const res = await fetch(`${API_URL}/api/recycle-bin/accounts/empty/all`, { method: 'DELETE', headers });
      if (res.ok) {
        setAcctRecycleBin([]);
      } else {
        const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
        alert(data.detail || 'Failed to empty accounts recycle bin');
      }
    } catch { alert('Failed to empty accounts recycle bin'); }
    finally { setAcctActionLoading(null); }
  };

  const toggleBulkDelete = () => {
    const newVal = !bulkDeleteEnabled;
    setBulkDeleteEnabled(newVal);
    localStorage.setItem('bulk_delete_enabled', String(newVal));
  };

  const handleRestore = async (recycleId, propName) => {
    if (!window.confirm(`Restore "${propName}" back to properties?`)) return;
    try {
      setActionLoading(recycleId);
      const res = await fetch(`${API_URL}/api/recycle-bin/properties/${recycleId}/restore`, {
        method: 'POST', headers
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (res.ok) {
        setRecycleBin(prev => prev.filter(item => item.id !== recycleId));
      } else {
        alert(data.detail || 'Failed to restore');
      }
    } catch { alert('Failed to restore property'); }
    finally { setActionLoading(null); }
  };

  const handlePermanentDelete = async (recycleId, propName) => {
    if (!window.confirm(`PERMANENTLY delete "${propName}"? This cannot be undone.`)) return;
    try {
      setActionLoading(recycleId);
      const res = await fetch(`${API_URL}/api/recycle-bin/properties/${recycleId}`, {
        method: 'DELETE', headers
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (res.ok) {
        setRecycleBin(prev => prev.filter(item => item.id !== recycleId));
      } else {
        alert(data.detail || 'Failed to delete');
      }
    } catch { alert('Failed to delete property'); }
    finally { setActionLoading(null); }
  };

  const handleEmptyBin = async () => {
    if (!window.confirm(`Empty entire recycle bin? All ${recycleBin.length} properties will be permanently deleted.`)) return;
    try {
      setActionLoading('empty-all');
      const res = await fetch(`${API_URL}/api/recycle-bin/properties/empty/all`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        setRecycleBin([]);
      } else {
        const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
        alert(data.detail || 'Failed to empty recycle bin');
      }
    } catch { alert('Failed to empty recycle bin'); }
    finally { setActionLoading(null); }
  };

  const filteredBin = recycleBin.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const prop = item.property_data || {};
    return (prop.propertyName || '').toLowerCase().includes(q) ||
      (prop.city || '').toLowerCase().includes(q) ||
      (prop.ownerName || '').toLowerCase().includes(q);
  });

  // iter286 — Accounts recycle-bin search filter.
  const filteredAcctBin = acctRecycleBin.filter(item => {
    if (!acctSearchQuery) return true;
    const q = acctSearchQuery.toLowerCase();
    const lead = item.lead_data || {};
    return (item.account_name || '').toLowerCase().includes(q) ||
      (lead.hotel_name || '').toLowerCase().includes(q) ||
      (lead.city || '').toLowerCase().includes(q) ||
      (lead.owner_name || '').toLowerCase().includes(q) ||
      (lead.grb_property_id || '').toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="data-management-settings">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Data Management</h2>
        <p className="text-sm text-gray-500">Manage bulk operations, recycle bin, and data safety settings</p>
      </div>

      {/* Bulk Delete Toggle */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-50 rounded-lg mt-0.5">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Bulk Delete</h3>
              <p className="text-sm text-gray-500 mt-1">Enable bulk selection and deletion of records in Hotel Onboarded (Leads) and Properties tabs.</p>
              <p className="text-xs text-gray-400 mt-1">Only Admin users can perform bulk delete operations.</p>
            </div>
          </div>
          <button
            onClick={toggleBulkDelete}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bulkDeleteEnabled ? 'bg-teal-600' : 'bg-gray-200'}`}
            data-testid="bulk-delete-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bulkDeleteEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {bulkDeleteEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800 text-sm">Bulk Delete is Enabled</h4>
            <p className="text-xs text-amber-700 mt-1">
              Checkboxes for selecting records are now visible in Leads and Properties tables. Deleted properties are moved to the Recycle Bin below.
            </p>
          </div>
        </div>
      )}

      {/* Recycle Bin Section */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="recycle-bin-section">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <RotateCcw size={20} className="text-orange-500" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Recycle Bin</h3>
                <p className="text-sm text-gray-500">
                  {recycleBin.length} deleted {recycleBin.length === 1 ? 'property' : 'properties'}
                </p>
              </div>
            </div>
            {recycleBin.length > 0 && (
              <button
                onClick={handleEmptyBin}
                disabled={actionLoading === 'empty-all'}
                className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="empty-recycle-bin-btn"
              >
                {actionLoading === 'empty-all' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Empty Bin
              </button>
            )}
          </div>

          {recycleBin.length > 3 && (
            <div className="mt-3 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search deleted properties..."
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="recycle-bin-search"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X size={14} className="text-gray-400" />
                </button>
              )}
            </div>
          )}
        </div>

        {loadingBin ? (
          <div className="p-8 text-center">
            <Loader2 size={24} className="animate-spin text-teal-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-2">Loading recycle bin...</p>
          </div>
        ) : filteredBin.length === 0 ? (
          <div className="p-8 text-center">
            <Trash2 size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {recycleBin.length === 0 ? 'Recycle bin is empty' : 'No matching properties found'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
            {filteredBin.map((item) => {
              const prop = item.property_data || {};
              const isLoading = actionLoading === item.id;
              return (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`recycle-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                        <Building2 size={18} className="text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{prop.propertyName || 'Unknown Property'}</h4>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                          {prop.propertyType && <span>{prop.propertyType}</span>}
                          {prop.city && <span>{prop.city}{prop.state ? `, ${prop.state}` : ''}</span>}
                          {prop.ownerName && <span>{prop.ownerName}</span>}
                          {(prop.totalRooms || prop.roomTypes?.length) && (
                            <span>{prop.totalRooms || prop.roomTypes?.length} rooms</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400">
                          <Clock size={12} />
                          <span>Deleted {formatDate(item.deleted_at)}</span>
                          {item.deleted_by_name && <span>by {item.deleted_by_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleRestore(item.id, prop.propertyName)}
                        disabled={isLoading}
                        className="px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`restore-btn-${item.id}`}
                      >
                        {isLoading ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(item.id, prop.propertyName)}
                        disabled={isLoading}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`permanent-delete-btn-${item.id}`}
                      >
                        {isLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* iter286 — Accounts Recycle Bin (mirrors the Properties bin above).
          Shows soft-deleted accounts with a Restore button; items are
          permanently purged by MongoDB TTL 30 days after deletion. */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="accounts-recycle-bin-section">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <RotateCcw size={20} className="text-orange-500" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Accounts Recycle Bin</h3>
                <p className="text-sm text-gray-500">
                  {acctRecycleBin.length} deleted {acctRecycleBin.length === 1 ? 'account' : 'accounts'} · auto-purged after 30 days
                </p>
              </div>
            </div>
            {acctRecycleBin.length > 0 && (
              <button
                onClick={handleAcctEmptyBin}
                disabled={acctActionLoading === 'empty-all'}
                className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="accounts-empty-recycle-bin-btn"
              >
                {acctActionLoading === 'empty-all' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Empty Bin
              </button>
            )}
          </div>

          {acctRecycleBin.length > 3 && (
            <div className="mt-3 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={acctSearchQuery}
                onChange={(e) => setAcctSearchQuery(e.target.value)}
                placeholder="Search deleted accounts..."
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="accounts-recycle-bin-search"
              />
              {acctSearchQuery && (
                <button onClick={() => setAcctSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X size={14} className="text-gray-400" />
                </button>
              )}
            </div>
          )}
        </div>

        {acctLoadingBin ? (
          <div className="p-8 text-center">
            <Loader2 size={24} className="animate-spin text-teal-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-2">Loading accounts recycle bin...</p>
          </div>
        ) : filteredAcctBin.length === 0 ? (
          <div className="p-8 text-center">
            <Trash2 size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {acctRecycleBin.length === 0 ? 'Accounts recycle bin is empty' : 'No matching accounts found'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
            {filteredAcctBin.map((item) => {
              const lead = item.lead_data || {};
              const name = item.account_name || lead.hotel_name || 'Unknown Account';
              const isLoading = acctActionLoading === item.id;
              return (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`accounts-recycle-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                        <Building2 size={18} className="text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{name}</h4>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                          {lead.grb_property_id && <span>{lead.grb_property_id}</span>}
                          {lead.city && <span>{lead.city}{lead.state ? `, ${lead.state}` : ''}</span>}
                          {lead.owner_name && <span>{lead.owner_name}</span>}
                          {lead.phone && <span>{lead.phone}</span>}
                        </div>
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400">
                          <Clock size={12} />
                          <span>Deleted {formatDate(item.deleted_at)}</span>
                          {item.deleted_by_name && <span>by {item.deleted_by_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleAcctRestore(item.id, name)}
                        disabled={isLoading}
                        className="px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`accounts-restore-btn-${item.id}`}
                      >
                        {isLoading ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                        Restore
                      </button>
                      <button
                        onClick={() => handleAcctPermanentDelete(item.id, name)}
                        disabled={isLoading}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`accounts-permanent-delete-btn-${item.id}`}
                      >
                        {isLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Safety Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-50 rounded-lg mt-0.5">
            <Shield size={20} className="text-blue-500" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Safety Rules</h3>
            <ul className="text-sm text-gray-600 mt-2 space-y-1.5">
              <li>Deleted properties are moved to the Recycle Bin instead of being permanently removed</li>
              <li>You can restore properties from the Recycle Bin at any time</li>
              <li>Restoring recovers all associated data (amenities, rooms, bank details)</li>
              <li>Only Admin users can manage the Recycle Bin</li>
              <li>Permanently deleted properties cannot be recovered</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;
